/**
 * What this conversation has used, on the composer.
 *
 * Round 5 (owner): "I don't need to know context per turn. Keep it for
 * overall session, like conductor" — and, on per-turn dollars, "should we
 * show dollar amount next to each turn?" Yes, quietly, and with a running
 * total somewhere a person can watch it. Both belong to the SESSION, not to
 * a turn.
 *
 * Round 7 moved the pair off the session header and onto the composer's
 * bottom edge, beside the model picker and the effort meter. Owner: "The
 * context and limits should come at the input text field like Conductor."
 * They should: how full the window is and what the conversation has cost are
 * the two numbers you weigh when you are about to spend more, and the moment
 * you are about to spend more is the moment your eyes are on the box you are
 * typing into. The header they left is a title bar, which nobody looks at
 * while composing.
 *
 * Two rules keep this honest:
 *  - the meter's denominator comes from the model metadata table
 *    (pricing.ts CONTEXT_WINDOWS). A model with no window on file renders NO
 *    meter, because a bar against a guessed denominator looks exactly as
 *    confident as a bar against a real one.
 *  - dollars appear only when a real API key is active. In keyless/fake mode
 *    nothing was billed, and a total that pretended otherwise would be
 *    inventing a bill.
 */

import { useMe } from '../../api/queries';
import type { ChatMessage } from '../../api/types';
import { formatCount } from '../../lib/format';
import { formatUsdApprox, sessionContext, sessionSpendUsd } from './pricing';

const ROW = 'text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary';

/**
 * How full the session's context is. The number is the newest turn's tokens
 * against the model's window (pricing.ts explains why it is not a sum).
 */
export function ContextMeter({ messages }: { messages: readonly ChatMessage[] }) {
  const context = sessionContext(messages);
  if (context === null) return null;

  const exact = context.fraction * 100;
  const rounded = Math.round(exact);
  // A conversation that has started is never drawn as empty: under half a
  // percent reads "<1%" rather than rounding down to nothing at all.
  const label = rounded === 0 && exact > 0 ? '<1%' : `${rounded}%`;

  return (
    <div
      data-testid="session-context"
      data-percent={rounded}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={rounded}
      aria-label="Context used in this conversation"
      title={`${formatCount(context.used)} of ${formatCount(context.window)} tokens of ${context.model}'s context window are in play. Older turns start dropping out as this fills.`}
      className={`flex shrink-0 items-center gap-1 ${ROW}`}
    >
      {/* The bar is the picture of the number beside it, so in a column
          narrowed past the compact threshold it is the first thing to go
          (index.css): at that width its 32px is a model name. */}
      <span
        aria-hidden
        className="lm-context-bar block h-1 w-7 overflow-hidden rounded-pill bg-inset"
      >
        <span
          className="block h-full rounded-pill bg-(--text-tertiary)"
          style={{ width: `${Math.max(exact, exact > 0 ? 4 : 0)}%` }}
        />
      </span>
      <span className="tabular-nums">{label}</span>
    </div>
  );
}

/** What the whole session has cost, at list price. */
export function SessionSpend({ messages }: { messages: readonly ChatMessage[] }) {
  const me = useMe();
  const spend = sessionSpendUsd(messages);
  if (spend === null || me.data?.api_key_present !== true) return null;
  return (
    <span
      data-testid="session-cost"
      title="What this whole conversation has cost, at list price: every answer in it, including ones re-cut at another depth."
      className={`shrink-0 tabular-nums ${ROW}`}
    >
      {formatUsdApprox(spend)}
    </span>
  );
}

/**
 * The pair, as the composer footer mounts them: pushed to the far end of the
 * controls row (ml-auto), so the reading order along the bottom edge is
 * "what will run" on the left, "what it is costing" on the right, then Send.
 *
 * shrink-0: when the column narrows it is the model name that truncates, not
 * the numbers. A half-drawn percentage says nothing.
 */
export function SessionMeter({ messages }: { messages: readonly ChatMessage[] }) {
  return (
    <div
      data-testid="session-meter"
      className="ml-auto flex shrink-0 items-center gap-2 pl-1"
    >
      <ContextMeter messages={messages} />
      <SessionSpend messages={messages} />
    </div>
  );
}
